import { FetchError } from "@medusajs/js-sdk"
import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query"
import {
  AdminProductApprovalListResponse,
  AdminProductApprovalResponse,
  AdminBulkApproveProductApprovalsInput,
  AdminBulkProductApprovalsResponse,
  AdminBulkRejectProductApprovalsInput,
  AdminRejectProductApprovalInput,
} from "../../b2b/types"
import { queryKeysFactory } from "../../lib/query-key-factory"
import { sdk } from "../../lib/client"

export const productApprovalQueryKeys = queryKeysFactory("product-approvals")

/**
 * GET /admin/product-approvals
 * List product approvals with pagination and filters
 */
export const useProductApprovals = (
  query?: Record<string, any>,
  options?: Omit<
    UseQueryOptions<
      AdminProductApprovalListResponse,
      FetchError,
      AdminProductApprovalListResponse,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) => {
  const filterQuery = new URLSearchParams(query ?? {}).toString()

  const fetchApprovals = async () =>
    sdk.client.fetch<AdminProductApprovalListResponse>(
      `/admin/product-approvals${filterQuery ? `?${filterQuery}` : ""}`,
      {
        method: "GET",
      }
    )

  const { data, ...rest } = useQuery({
    queryKey: productApprovalQueryKeys.list(query),
    queryFn: fetchApprovals,
    ...options,
  })
  return { ...data, ...rest }
}

/**
 * GET /admin/product-approvals/:id
 * Get single product approval detail
 */
export const useProductApproval = (
  id: string,
  options?: Omit<
    UseQueryOptions<
      AdminProductApprovalResponse,
      FetchError,
      AdminProductApprovalResponse,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) => {
  const fetchApproval = async () =>
    sdk.client.fetch<AdminProductApprovalResponse>(
      `/admin/product-approvals/${id}`,
      {
        method: "GET",
      }
    )

  const { data, ...rest } = useQuery({
    queryKey: productApprovalQueryKeys.detail(id),
    queryFn: fetchApproval,
    enabled: !!id,
    ...options,
  })
  return { ...data, ...rest }
}

/**
 * POST /admin/product-approvals/:id/approve
 * Approve a pending product approval
 */
export const useApproveProductApproval = (
  id: string,
  options?: UseMutationOptions<AdminProductApprovalResponse, FetchError, void>
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      sdk.client.fetch<AdminProductApprovalResponse>(
        `/admin/product-approvals/${id}/approve`,
        {
          method: "POST",
        }
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: productApprovalQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: productApprovalQueryKeys.detail(id),
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

/**
 * POST /admin/product-approvals/:id/reject
 * Reject a pending product approval with reason
 */
export const useRejectProductApproval = (
  id: string,
  options?: UseMutationOptions<
    AdminProductApprovalResponse,
    FetchError,
    AdminRejectProductApprovalInput
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: AdminRejectProductApprovalInput) =>
      sdk.client.fetch<AdminProductApprovalResponse>(
        `/admin/product-approvals/${id}/reject`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
        }
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: productApprovalQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: productApprovalQueryKeys.detail(id),
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

/**
 * POST /admin/product-approvals/bulk/approve
 * Bulk-approve pending approvals
 */
export const useBulkApproveProductApprovals = (
  options?: UseMutationOptions<
    AdminBulkProductApprovalsResponse,
    FetchError,
    AdminBulkApproveProductApprovalsInput
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: AdminBulkApproveProductApprovalsInput) =>
      sdk.client.fetch<AdminBulkProductApprovalsResponse>(
        `/admin/product-approvals/bulk/approve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
        }
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: productApprovalQueryKeys.lists(),
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

/**
 * POST /admin/product-approvals/bulk/reject
 * Bulk-reject pending approvals with a shared rejection reason
 */
export const useBulkRejectProductApprovals = (
  options?: UseMutationOptions<
    AdminBulkProductApprovalsResponse,
    FetchError,
    AdminBulkRejectProductApprovalsInput
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: AdminBulkRejectProductApprovalsInput) =>
      sdk.client.fetch<AdminBulkProductApprovalsResponse>(
        `/admin/product-approvals/bulk/reject`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
        }
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: productApprovalQueryKeys.lists(),
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}
